import Vue from 'vue'
import {make} from 'vuex-pathify'
import {ptracGet, ptracPatch} from '@/utilities/ptracApi'
import {OK} from 'http-status-codes'
import Constants from '@/utilities/constants'
import dayjs from 'dayjs'
import {formatDate, nextBusinessDay, sortBy} from '@/utilities/utils'
import _ from 'lodash'
import {autoRoutingGet, autoRoutingPost} from '@/utilities/autoRoutingApi'

let initialState = {
  solverParams: {
    isSimulation : false,
    dateToRouteInspectors: nextBusinessDay(),
    businessDaysAgo: 30,
    canReassignManualAssignments: false,
    canReassignReinspectionAssignments: false,
    canAssignUnassignedReinspections: true,
    // weights act as multiplier to the score calculated for each assignment by solver
    // eligibility and location scores are both normalized, so the weighting has a direct relationship
    // workload penalty is not normalized (for now), where more inspections results in a higher (more -ve) workload penalty
    // so this weighting is not directly related to the other two - maybe need a better solution
    eligibilityWeight: 1,
    locationWeight: 8,
    workloadWeight: 300,
    serviceAreaIdsToRoute: [],
    departmentIdToRoute: undefined
  },

  //TODO: Refactor out inspectorTable state (results, selectedInspectionIds, query, tableData getters)?
  // results from Optaplanner solver
  solverStatus: Constants.INSPECTOR_ROUTING.SOLVER_STATUS.NOT_SOLVING,
  results: [],
  score: {},
  resultsRoutingDate: null,
  resultsTimestamp: null,
  solverFailed: false,

  // Inspection Table shared state
  selectedInspectionIds: new Set(),
  query: "",

  // from ptrac query for simulation mode
  actualAssignmentsOnSimulatedDate: [],

  // Stats calculated from solution
  stats: {
    numMatchingAssignments: 0,
    numReinspections: 0,
    numReinspectionsAssigned: 0,
    numReinspectionsManuallyAssigned: 0,
    numReinspectionsAssignedMostRecentInspector: 0,
    numInspectionsAssigned: 0,
    numInspectionsManuallyAssigned:0
  }
}

const state = Vue.util.extend({}, JSON.parse(JSON.stringify(initialState)))

const getters = make.getters(state)

const mutations = make.mutations(state)

getters.inspectorsForSelect = (state, getters, rootState, rootGetters) => {
  let inspectorUsers = rootGetters['users/inspectors']
  let inspectorsForSelect = inspectorUsers.map(user => {
    let inspector = {
      label: user.name,
      value:user.id,
      available: true
    }
    if (user.daysOff && user.daysOff.find(date => formatDate(date) === formatDate(state.solverParams.dateToRouteInspectors))) {
      inspector.available = false
    }
    return inspector;
  })

  inspectorsForSelect = sortBy(inspectorsForSelect, ('label'))

  inspectorsForSelect.unshift({
    label: Constants.INSPECTOR_ROUTING.UNASSIGNED.LABEL,
    value: Constants.INSPECTOR_ROUTING.UNASSIGNED.VALUE,
    available: true
  })
  return  inspectorsForSelect
}

getters.getInspectionsByInspector = (state) => {
  let inspectionsByInspector = {}// {rowId: int, inspector: id, children: [ assignments{rowId: int, inspector: id, inspection: id} ] }
  let rowId = 1

  for (const inspection of state.results) {
    // Add inspector row if it doesn't already exist - needs a rowId and a children field (inspections) for nested table
    if (!inspectionsByInspector[inspection.assignedInspector.id]) {
      const inspectorRow = {
        "rowId": rowId,
        "inspectorId": inspection.assignedInspector.id,
        "inspectorName": inspection.assignedInspector.name,
        "inspections": []
      }
      inspectionsByInspector[inspection.assignedInspector.id] = inspectorRow
      rowId++
    }

    //Add inspection assignment as child of inspector
    let inspectionRow = {"rowId": rowId}
    inspectionRow = Object.assign(inspectionRow, inspection)
    inspectionsByInspector[inspection.assignedInspector.id].inspections.push(inspectionRow)
    rowId++
  }

  return Object.values(inspectionsByInspector)
}

getters.getInspectionsByProject = (state) => {
  let inspectionsByProject = {}
  let rowId = 1


  for (const inspection of state.results) {
    // Add project row if it doesnt already exist - needs a rowId and a children field (inspections) for nested table
    if (!inspectionsByProject[inspection.project.name]) {
      const projectRow = {
        "rowId": rowId,
        "projectName": inspection.project.name,
        "projectId": inspection.project.id,
        "inspections": []
      }
      inspectionsByProject[inspection.project.name] = projectRow
      rowId++
    }

    //Add inspection as child of projectName
    let inspectionRow = {"rowId": rowId}
    inspectionRow = Object.assign(inspectionRow, inspection)
    inspectionsByProject[inspection.project.name].inspections.push(inspectionRow)
    rowId++
  }

  return Object.values(inspectionsByProject)
}

mutations.reassignInspection = (state, payload) => {
  const inspector = payload.assignedInspector
  const inspection = state.results.find(insp => insp.ptracTaskId === payload.inspectionId)
  if (inspection) {
    if (inspector && inspector.value !== Constants.INSPECTOR_ROUTING.UNASSIGNED.VALUE) {
      if (!inspection.manuallyAssignedInspector
        || (inspection.manuallyAssignedInspector && inspection.manuallyAssignedInspector.id !== inspector.value)) {
        inspection.manuallyReassigned = true
      }
      inspection.assignedInspector = {id: inspector.value, name: inspector.label}
    } else {
      if (inspection.manuallyAssignedInspector) {
        inspection.manuallyReassigned = true
      }
      inspection.assignedInspector = { id: Constants.INSPECTOR_ROUTING.UNASSIGNED.VALUE, name: Constants.INSPECTOR_ROUTING.UNASSIGNED.LABEL }
    }

    state.selectedInspectionIds.add(payload.inspectionId)
    state.results = _.clone(state.results)
  } else {
    console.error(` ${inspection.inspectionType} (${payload.inspectionId}) not found in results`)
  }
}

mutations.bulkReassignInspections = (state, payload) => {
  const inspector = payload.assignedInspector
  for (const taskId of payload.inspectionIds) {
    const inspection = state.results.find(insp => insp.ptracTaskId === taskId)
    if (inspection) {
      if (inspector && inspector.value !== Constants.INSPECTOR_ROUTING.UNASSIGNED.VALUE) {
        if (!inspection.manuallyAssignedInspector
          || (inspection.manuallyAssignedInspector && inspection.manuallyAssignedInspector.id !== inspector.value)) {
          inspection.manuallyReassigned = true
        }
        inspection.assignedInspector = { id: inspector.value, name: inspector.label }
      } else {
        if (inspection.manuallyAssignedInspector) {
          inspection.manuallyReassigned = true
        }
        inspection.assignedInspector = { id: Constants.INSPECTOR_ROUTING.UNASSIGNED.VALUE, name: Constants.INSPECTOR_ROUTING.UNASSIGNED.LABEL }
      }

      state.selectedInspectionIds.add(taskId)
      state.results = _.clone(state.results)
    } else {
      console.error(`Inspection ${taskId} not found in results`)
    }
  }
}

mutations.updateInspectionCoordinates = (state, payload) => {
  for (const project of payload) {
    const inspections = state.results.filter(inspection => inspection.project.id === project.id)

    if (inspections.length > 0) {
      inspections.forEach(i => i.inspectionCoordinates = [project.address.lat, project.address.lng])
    }
  }
}

mutations.resetResults = () => {
  state.solverStatus = Constants.INSPECTOR_ROUTING.SOLVER_STATUS.NOT_SOLVING
  state.results = []
  state.score = {}
  state.actualAssignmentsOnSimulatedDate = []
  state.resultsRoutingDate = null
  state.resultsTimestamp = null
  state.solverFailed = false
  mutations.resetStats()
}

mutations.resetStats = () => {
    state.stats.numMatchingAssignments = 0
    state.stats.numReinspections = 0
    state.stats.numReinspectionsAssigned = 0
    state.stats.numReinspectionsManuallyAssigned= 0
    state.stats.numReinspectionsAssignedMostRecentInspector = 0
    state.stats.numInspectionsAssigned = 0
    state.stats.numInspectionsManuallyAssigned = 0
}

mutations.reset = (state) => {
  state = Vue.util.extend({}, JSON.parse(JSON.stringify(initialState)))
}

export default {
  namespaced: true,
  getters,
  state,
  mutations,
  actions: {
    async solveRouting({dispatch, rootGetters}) {
      state.solverStatus = Constants.INSPECTOR_ROUTING.SOLVER_STATUS.PREPARING
      let response = await autoRoutingPost('solve-routing', state.solverParams)
      if (OK === response.status) {
        state.solverStatus = Constants.INSPECTOR_ROUTING.SOLVER_STATUS.SOLVING
        const problemId = response.data

        // Solver started, start polling checkForSolution and wait for solution
        let solutionResponse
        do {
          solutionResponse = await autoRoutingGet(`check-for-solution?problemId=${problemId}`)
          if (OK === solutionResponse.status) {
            if (solutionResponse.data.solverStatus === Constants.INSPECTOR_ROUTING.SOLVER_STATUS.NOT_SOLVING && !solutionResponse.data.solution) {
              state.solverFailed = true
              return
            }

            if (solutionResponse.data.solverStatus === Constants.INSPECTOR_ROUTING.SOLVER_STATUS.NOT_SOLVING && solutionResponse.data.solution) {
              state.results = solutionResponse.data.solution
              state.score =  solutionResponse.data.score
              state.resultsRoutingDate = state.solverParams.dateToRouteInspectors
              state.resultsTimestamp = new Date()
              break
            }

          } else {
            state.solverFailed = true
            return
          }

          await new Promise(resolve => setTimeout(resolve, 2000))
        } while (true)

      } else {
        state.solverFailed = true
      }

      state.solverStatus = Constants.INSPECTOR_ROUTING.SOLVER_STATUS.PROCESSING
      // fetch companies for client and builder and add them as fields in result objects
      let builderIds = new Set()
      let clientIds = new Set()
      for (const assignment of state.results) {
        clientIds.add(assignment.job.clientId)
        builderIds.add(assignment.job.builderId)
      }
      await dispatch('companies/fetchByIdsToMap', { ids: [...clientIds], type: Constants.COMPANY.TYPES.CLIENT.toLowerCase() }, {root: true})
      await dispatch('companies/fetchByIdsToMap', { ids: [...builderIds], type: Constants.COMPANY.TYPES.BUILDER.toLowerCase() }, {root: true})
      await dispatch('tasks/templates/fetchAll', null, {root: true})

      const clientMap = rootGetters['companies/clientMap']
      const builderMap = rootGetters['companies/builderMap']
      const templates = rootGetters['tasks/templates/activeTemplates']

      for (const assignment of state.results) {
        const client = clientMap[assignment.job.clientId]
        if (client) {
          assignment.job.clientName = client.name
        }
        const builder = builderMap[assignment.job.builderId]
        if (builder) {
          assignment.job.builderName = builder.name
        }

        const template = templates.find(template => template.value === assignment.ptracTask.templateId)
        if (template) {
          assignment.ptracTask.templateName = template.label
        }
      }

      if (state.solverParams.isSimulation) {
        response = await ptracGet(`tasks/findADaysInspectionTasks?date=${dayjs(state.solverParams.dateToRouteInspectors, Constants.DATE.URL_FORMAT.DAYJS)}`)
        if (OK === response.status) {
          state.actualAssignmentsOnSimulatedDate = response.data
        } else {
          console.error("Couldn't get actual inspections from simulated date")
        }
      }

      await dispatch('populateSolutionStats')
      state.solverStatus = Constants.INSPECTOR_ROUTING.SOLVER_STATUS.LOADING_TABLES
    },

    populateSolutionStats({commit, rootGetters}) {
      commit('resetStats')
      for (const assignment of state.results) {

        if (assignment.assignedInspector && assignment.assignedInspector.id !== Constants.INSPECTOR_ROUTING.UNASSIGNED.VALUE) {
          state.stats.numInspectionsAssigned++
        }

        if (assignment.manuallyAssignedInspector) {
          state.stats.numInspectionsManuallyAssigned++
        }

        if (assignment.reinspection) {
          state.stats.numReinspections++

          if (assignment.assignedInspector && assignment.assignedInspector.id !== Constants.INSPECTOR_ROUTING.UNASSIGNED.VALUE) {
            state.stats.numReinspectionsAssigned++
          }
          if (assignment.manuallyAssignedInspector) {
            state.stats.numReinspectionsManuallyAssigned++
          } else if (assignment.assignedInspector && assignment.mostRecentAvailableInspectorForReinspection
                      && assignment.mostRecentAvailableInspectorForReinspection.id === assignment.assignedInspector.id) {
            state.stats.numReinspectionsAssignedMostRecentInspector++
          }
        }

        // For simulations, add actual assignees to results, then compare
        if (state.solverParams.isSimulation) {
          const actualAssignment = state.actualAssignmentsOnSimulatedDate.find(actual => actual.id === assignment.ptracTaskId)
          assignment.actualInspector = {}
          if (actualAssignment.assigneeId) {
            assignment.actualInspector.id = actualAssignment.assigneeId
            assignment.actualInspector.name = rootGetters['users/displayName'](actualAssignment.assigneeId)
          } else {
            assignment.actualInspector.id = Constants.INSPECTOR_ROUTING.UNASSIGNED.VALUE
            assignment.actualInspector.name = Constants.INSPECTOR_ROUTING.UNASSIGNED.LABEL
          }

          if (assignment.assignedInspector && assignment.assignedInspector.id === assignment.actualInspector.id) {
            state.stats.numMatchingAssignments++
          }
        }
      }
    },
    async saveAssignmentsToPtrac({state}, ptracIdsToSave) {
      const payload = []
      for (const assignment of state.results) {
        if (ptracIdsToSave.includes(assignment.ptracTaskId)) {
          // if task was previously manually assigned, and wasnt re-assigned in the UI, don't include in payload
          if (!!assignment.manuallyAssignedInspector && !assignment.manuallyReassigned) {
            continue
          }

          // if task has no assigned inspector (or unassigned), but hasn't been reassigned, don't include in payload
          if ((!assignment.assignedInspector || assignment.assignedInspector.id === Constants.INSPECTOR_ROUTING.UNASSIGNED.VALUE)
            && !assignment.manuallyReassigned) {
            continue
          }

          payload.push({
            "taskId": assignment.ptracTaskId,
            "assigneeId": assignment.assignedInspector.id === Constants.INSPECTOR_ROUTING.UNASSIGNED.VALUE ? null : assignment.assignedInspector.id
          })

          // For any inspections that are being saved, set manuallyReassigned to false on saving to remove green tooltip
          // and set manuallyAssignedInspector to assignedInspector to add/update Orange tooltip
          assignment.manuallyReassigned = false
          assignment.manuallyAssignedInspector = assignment.assignedInspector.id === Constants.INSPECTOR_ROUTING.UNASSIGNED.VALUE ? null : assignment.assignedInspector.id
          state.results = _.clone(state.results)
        }
      }
      return await ptracPatch('tasks/bulkAssign', payload)
    }
  }
}
